import { useEffect, useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useAppDispatch, useAppSelector } from '../../../app/typedReduxHooks'
import { useHasEntitlement } from '../../../pages/OrganizationProfilePage/planEntitlements'
import { clearWorkPrescriberProfileId } from '../../../pages/NewScriptPage/ScriptForm/PrescriberSlice'
import { FeatureKey } from '../../../types/organization-entitlements'
import { buildPrescribingLocation, type PrescribingLocation } from '../utils/prescribingLocation'
import { useGetPrescriberLocationProfilesQuery } from '../prescriberLocationProfilesSlice'

export type PrescribingLocationContext = {
  prescribingLocation: PrescribingLocation | null
}

export type { PrescribingLocation }

export const usePrescribingLocation = (): PrescribingLocationContext => {
  const dispatch = useAppDispatch()
  const workPrescriberProfileId = useAppSelector(
    (state) => state.prescriber.workPrescriberProfileId
  )
  const currentClinic = useAppSelector((state) => state.clinic.currentClinic)
  const hasMultipleLocations = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)
  const { data: prescriberProfiles = [], isSuccess } = useGetPrescriberLocationProfilesQuery(
    hasMultipleLocations ? undefined : skipToken
  )

  const workLocationProfile = useMemo(() => {
    if (!hasMultipleLocations || !workPrescriberProfileId) return undefined
    return prescriberProfiles.find((p) => p.joined && p.id === workPrescriberProfileId)
  }, [hasMultipleLocations, prescriberProfiles, workPrescriberProfileId])

  useEffect(() => {
    if (!hasMultipleLocations || !workPrescriberProfileId || !isSuccess) return

    const isValidProfile = prescriberProfiles.some(
      (profile) => profile.joined && profile.id === workPrescriberProfileId
    )

    if (!isValidProfile) {
      dispatch(clearWorkPrescriberProfileId())
    }
  }, [dispatch, hasMultipleLocations, isSuccess, prescriberProfiles, workPrescriberProfileId])

  const prescribingLocation = useMemo(() => {
    if (!currentClinic) return null

    return buildPrescribingLocation(workLocationProfile, currentClinic)
  }, [currentClinic, hasMultipleLocations, workLocationProfile])

  return { prescribingLocation }
}
